import { useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useDialogEscapeFallback } from '@/components/lw';
import {
  cancelRecaptureDraft, downloadRecaptureBlob, downloadRecaptureEvidence, getRecaptureWorkspace,
  reverseRecapture, saveRecaptureDraft, submitRecapture, uploadRecaptureEvidence,
  type RecaptureAllocationInput, type RecaptureContext, type RecaptureRecord, type RecaptureWorkspace
} from '@/services/recaptureService';
import { moneyCents, parseMoneyInput } from '../../../supabase/functions/_shared/payout-settlement';
import { RecaptureOriginPicker } from './RecaptureOriginPicker';

const currency = (value: number | null) => value === null ? 'Unavailable' : new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD'
}).format(value);

interface Props {
  workspace: RecaptureWorkspace;
  onRefresh: () => Promise<void>;
}

class CancelledRecaptureSubmission extends Error {}

export function RecapturePanel({ workspace, onRefresh }: Props) {
  const [context, setContext] = useState<RecaptureContext | null>(null);
  const [draft, setDraft] = useState<RecaptureRecord | null>(null);
  const [replacementOf, setReplacementOf] = useState<string>();
  const [claim, setClaim] = useState('');
  const [invoice, setInvoice] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [payoffDate, setPayoffDate] = useState('');
  const [notes, setNotes] = useState('');
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [receipt, setReceipt] = useState<RecaptureRecord | null>(null);
  const [reversalReason, setReversalReason] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [unconfirmed, setUnconfirmed] = useState(false);
  const locked = useRef(false);
  const request = useRef(0);
  const submitKey = useRef(crypto.randomUUID());
  const refreshButton = useRef<HTMLButtonElement>(null);
  const receiptTrigger = useRef<HTMLButtonElement | null>(null);
  const pendingSubmission = useRef<{ id: string; version: number; token: string; key: string } | null>(null);
  const receiptContent = useRef<HTMLDivElement>(null);
  const handleReceiptOpenChange = (open: boolean) => {
    if (!open && !busy) {
      setReceipt(null);
    }
  };
  useDialogEscapeFallback(receiptContent, () => handleReceiptOpenChange(false));

  const perform = async (action: () => Promise<void>) => {
    if (locked.current) {
      return;
    }
    locked.current = true;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await action();
    } catch (cause) {
      setError((cause as { code?: string })?.code === '23505'
        ? 'An active record already exists for this commission. Refresh the register and open that record.'
        : cause instanceof Error ? cause.message : (cause as { message?: string })?.message || 'Unable to save recapture');
    } finally {
      locked.current = false;
      setBusy(false);
    }
  };

  const openOrigin = async (payoutId: string, record: RecaptureRecord | null = null, replacement?: string) => {
    const current = ++request.current;
    await perform(async () => {
      const result = await getRecaptureWorkspace(payoutId);
      if (current !== request.current) {
        return;
      }
      const existing = result.records.find(row => row.status === 'draft'
        && (record ? row.id === record.id : row.origin_payout_id === payoutId)) || null;
      if (record && !existing) {
        throw new Error('This draft is no longer editable. Refresh the register.');
      }
      setContext(result.context);
      setDraft(existing);
      setReplacementOf(replacement);
      setClaim(existing?.claim_amount?.toFixed(2) || '');
      setInvoice(existing?.invoice_reference || '');
      setInvoiceDate(existing?.invoice_date || '');
      setPayoffDate(existing?.payoff_date || '');
      setNotes(existing?.notes || '');
      setAmounts(Object.fromEntries((existing?.draft_allocations || []).map(a => [a.kind === 'profitability' ? 'profitability' : a.payout_id!, a.amount.toFixed(2)])));
      submitKey.current = crypto.randomUUID();
      pendingSubmission.current = null;
      setUnconfirmed(false);
      setReceipt(null);
    });
  };

  const allocationInputs: RecaptureAllocationInput[] = [];
  let invalidAmount = false;
  let allocatedCents = 0;
  for (const [key, input] of Object.entries(amounts)) {
    if (!input.trim()) {
      continue;
    }
    const value = parseMoneyInput(input);
    if (value === null) {
      invalidAmount = true;
      continue;
    }
    if (value > 0) {
      allocatedCents += moneyCents(value);
      allocationInputs.push(key === 'profitability' ? { kind: 'profitability', amount: value } : { kind: 'commission', payout_id: key, amount: value });
    }
  }
  const target = context?.origin.gross_commission || 0;
  const remaining = (moneyCents(target) - allocatedCents) / 100;
  const claimAmount = claim.trim() ? parseMoneyInput(claim) : null;
  const claimMatches = claimAmount !== null && claimAmount > 0 && context?.origin.premium !== null
    && moneyCents(claimAmount) === moneyCents(context?.origin.premium || 0);
  const claimHelp = context?.origin.purchase_price === null || (context?.origin.purchase_price ?? 0) <= 0
    ? 'The finalized reconciliation is missing a usable purchase price. Accounting must review the reconciliation before submitting.'
    : context?.origin.premium === null || (context?.origin.premium ?? 0) <= 0
      ? 'The finalized reconciliation has no positive investor premium. Accounting must review the reconciliation before submitting.'
      : claim && !claimMatches
        ? 'Investor claim must exactly match the finalized premium. Review the reconciliation and investor invoice before submitting.'
        : 'The claim must match exactly.';
  const allocationError = (key: string, available: number | null) => {
    const raw = amounts[key];
    if (!raw?.trim()) {
      return '';
    }
    const parsed = parseMoneyInput(raw);
    if (parsed === null) {
      return 'Enter an amount with at most two decimal places.';
    }
    return parsed > 0 && (available === null || moneyCents(parsed) > moneyCents(available)) ? 'Amount exceeds the available balance.' : '';
  };
  const exceedsSource = allocationInputs.some(allocation => allocation.kind === 'profitability'
    ? Boolean(allocationError('profitability', context?.profitability.available ?? null))
    : !context?.sources.some(source => source.payout_id === allocation.payout_id)
      || Boolean(allocationError(allocation.payout_id!, context?.sources.find(source => source.payout_id === allocation.payout_id)?.remaining ?? null)));
  const canSubmit = workspace.enabled && context?.origin.eligible && claimMatches && !invalidAmount && !exceedsSource && remaining === 0;

  const showPosted = async (id: string) => {
    const posted = await getRecaptureWorkspace();
    const record = posted.records.find(row => row.id === id);
    if (record?.status === 'cancelled') {
      pendingSubmission.current = null;
      setUnconfirmed(false);
      setContext(null);
      setDraft(null);
      await onRefresh();
      throw new CancelledRecaptureSubmission('Recapture was cancelled before submission. No balances were deducted.');
    }
    if (!record || (record.status !== 'submitted' && record.status !== 'reversed')) {
      return false;
    }
    pendingSubmission.current = null;
    setUnconfirmed(false);
    receiptTrigger.current = null;
    setReceipt(record);
    setContext(null);
    setDraft(null);
    setMessage('Recapture submitted. Balances were updated.');
    await onRefresh();
    return true;
  };

  const finishSubmission = async () => {
    const attempt = pendingSubmission.current;
    if (!attempt) {
      return;
    }
    try {
      // Reuse this key after a lost response; do not create another draft.
      await submitRecapture(attempt.id, attempt.version, attempt.token, attempt.key);
      if (!await showPosted(attempt.id)) {
        throw new Error('Submission has not been confirmed. Check its status before editing.');
      }
    } catch (cause) {
      if (cause instanceof CancelledRecaptureSubmission) {
        throw cause;
      }
      try {
        if (await showPosted(attempt.id)) {
          return;
        }
      } catch (statusError) {
        if (statusError instanceof CancelledRecaptureSubmission) {
          throw statusError;
        }
      }
      const code = (cause as { code?: string })?.code;
      if (code === 'P0001' || code === '40001' || code === '23505' || code === '42501') {
        pendingSubmission.current = null;
        setUnconfirmed(false);
      } else {
        setUnconfirmed(true);
      }
      throw cause;
    }
  };

  const save = async (post: boolean) => {
    if (!context) {
      return;
    }
    await perform(async () => {
      const id = await saveRecaptureDraft({
        id: draft?.id, version: draft?.version, origin_payout_id: context.origin.payout_id,
        reason: 'epo', claim_amount: claimAmount, invoice_reference: invoice,
        invoice_date: invoiceDate || null, payoff_date: payoffDate || null, notes,
        allocations: allocationInputs, replacement_of: replacementOf
      });
      const refreshed = await getRecaptureWorkspace(context.origin.payout_id);
      const saved = refreshed.records.find(row => row.id === id);
      if (!saved) {
        throw new Error('Saved draft could not be loaded. Refresh the register.');
      }
      if (saved.status !== 'draft' || saved.version !== (draft ? draft.version + 1 : 2)) {
        await onRefresh();
        throw new Error('The draft changed after saving. Reopen it from the register before submitting.');
      }
      setDraft(saved);
      if (post) {
        // Use the balances actually reviewed on screen, not the newer refetch.
        pendingSubmission.current = { id, version: saved.version, token: context.balance_token, key: submitKey.current };
        await finishSubmission();
      } else {
        setMessage('Draft saved. No balances have been deducted.');
      }
      await onRefresh();
    });
  };

  const renderAmount = (key: string, label: string, available: number | null) => {
    const fieldError = allocationError(key, available);
    return <div className="space-y-1">
      <Label htmlFor={`recapture-${key}`}>{label}</Label>
      <Input id={`recapture-${key}`} inputMode="decimal" value={amounts[key] || ''}
        placeholder="0.00" disabled={busy || unconfirmed || !workspace.enabled || available === null || available <= 0.01}
        aria-invalid={Boolean(fieldError)} aria-describedby={`recapture-${key}-help`}
        onChange={event => setAmounts(current => ({ ...current, [key]: event.target.value }))} />
      <p id={`recapture-${key}-help`} className={fieldError ? 'text-xs text-destructive' : 'text-xs text-muted-foreground'}>
        {fieldError || `Available: ${currency(available)}`}
      </p>
    </div>;
  };

  return <div className="min-w-0 space-y-4">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div><h2 className="text-xl font-semibold">Recapture</h2><p className="text-sm text-muted-foreground">Apply an EPO commission recapture to balances owed to the loan officer.</p></div>
      <Button ref={refreshButton} variant="outline" disabled={busy} onClick={() => void perform(async () => {
        await onRefresh();
        if (context) {
          const result = await getRecaptureWorkspace(context.origin.payout_id);
          setContext(result.context);
        }
        setMessage('Balances refreshed. Review your amounts before submitting.');
      })}><RefreshCw className="mr-2 h-4 w-4" />Refresh balances</Button>
    </div>
    {error && <p role="alert" className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">{error}</p>}
    {message && <p role="status" className="text-sm text-muted-foreground">{message}</p>}
    {unconfirmed && <div role="alert" className="space-y-2 rounded-md border p-3 text-sm">
      <p>The submission response was interrupted. Confirm its status before changing allocations.</p>
      <Button variant="outline" disabled={busy} onClick={() => void perform(async () => {
        if (pendingSubmission.current && !await showPosted(pendingSubmission.current.id)) {
          await finishSubmission();
        }
      })}>Check submission status</Button>
    </div>}
    {!workspace.enabled && <p className="rounded-md border p-3 text-sm">New recaptures are disabled. Existing deductions and history remain in effect; submitted records can still be reversed.</p>}
    {workspace.enabled && <Card>
      <CardHeader><CardTitle className="text-base">Originating commission</CardTitle><CardDescription>Choose a finalized banked loan whose commission was already paid.</CardDescription></CardHeader>
      <CardContent className="space-y-3">
        <RecaptureOriginPicker
          origins={workspace.origins.filter(origin => !workspace.records.some(r => r.origin_payout_id === origin.payout_id && r.status === 'submitted'))}
          selectedOrigin={context?.origin || null} disabled={busy || unconfirmed}
          onSelect={payoutId => void openOrigin(payoutId)} />
        {workspace.origins.length === 0 && <p className="text-sm text-muted-foreground">No eligible paid banked commissions are available.</p>}
      </CardContent>
    </Card>}
    {context && <Card>
      <CardHeader><CardTitle className="text-base">{context.origin.loan_number} · {context.origin.recipient_name}</CardTitle><CardDescription>LO recapture target: {currency(target)} gross commission, before E&amp;O and excluding reimbursements.</CardDescription></CardHeader>
      <CardContent className="space-y-5">
        <fieldset disabled={busy || unconfirmed || !workspace.enabled} className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1"><Label htmlFor="recapture-reason">Reason</Label><Input id="recapture-reason" value="Early payoff (EPO)" readOnly /></div>
          <div className="space-y-1"><Label htmlFor="recapture-claim">Investor claim amount</Label><Input id="recapture-claim" inputMode="decimal" value={claim} disabled={busy || !workspace.enabled} onChange={e => setClaim(e.target.value)} aria-invalid={Boolean(claim) && !claimMatches} aria-describedby="recapture-claim-help" />
            <p id="recapture-claim-help" className={`text-xs ${claim && !claimMatches ? 'text-destructive' : 'text-muted-foreground'}`}>Finalized premium: {currency(context.origin.premium)}. {claimHelp}</p></div>
          <div className="space-y-1"><Label htmlFor="recapture-invoice">Invoice reference</Label><Input id="recapture-invoice" value={invoice} maxLength={200} disabled={busy} onChange={e => setInvoice(e.target.value)} /></div>
          <div className="space-y-1"><Label htmlFor="recapture-invoice-date">Invoice date</Label><Input id="recapture-invoice-date" type="date" value={invoiceDate} disabled={busy} onChange={e => setInvoiceDate(e.target.value)} /></div>
          <div className="space-y-1"><Label htmlFor="recapture-payoff-date">Payoff date</Label><Input id="recapture-payoff-date" type="date" value={payoffDate} disabled={busy} onChange={e => setPayoffDate(e.target.value)} /></div>
        </fieldset>
        <p className="text-xs text-muted-foreground">Purchase date: {context.origin.purchase_date || 'Unavailable'} · EPO clear date: {context.origin.epo_clear_date || 'Unavailable'} · Collected principal: {currency(context.origin.principal_collected)}</p>
        <div className="space-y-1"><Label htmlFor="recapture-notes">Accounting notes</Label><Textarea id="recapture-notes" value={notes} maxLength={5000} disabled={busy || unconfirmed || !workspace.enabled} onChange={e => setNotes(e.target.value)} /></div>
        <div className="space-y-2 rounded-md border p-3">
          <Label htmlFor="recapture-evidence">Supporting invoice or payment history</Label>
          <Input id="recapture-evidence" type="file" accept=".pdf,.xlsx" disabled={!draft || busy || unconfirmed || !workspace.enabled}
            onChange={event => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file && draft) {
                void perform(async () => {
                  await uploadRecaptureEvidence(draft.id, file);
                  const refreshed = await getRecaptureWorkspace(context.origin.payout_id);
                  const saved = refreshed.records.find(row => row.id === draft.id);
                  if (!saved || saved.version !== draft.version + 1) {
                    await onRefresh();
                    throw new Error('Evidence was added, but the draft also changed. Reopen it from the register before saving.');
                  }
                  setDraft(saved);
                  await onRefresh();
                  setMessage('Supporting evidence added.');
                });
              }
            }} />
          <p className="text-xs text-muted-foreground">{draft ? 'PDF or XLSX, up to 20 MB each. Completed files are retained with the audit history.' : 'Save a draft first to add optional supporting files.'} Only Accounting can access these files.</p>
          {draft?.attachments?.map(file => <Button key={file.id} variant="link" className="h-auto max-w-full whitespace-normal break-all p-0 text-left" disabled={busy}
            onClick={() => void perform(() => downloadRecaptureEvidence(file))}>{file.file_name}</Button>)}
        </div>
        <div className="rounded-md border p-3">
          {renderAmount('profitability', 'Deduct from LO Profitability', context.profitability.available)}
          {!context.profitability.complete && <p className="mt-2 text-xs text-muted-foreground">Historical profitability needs review. Commission balances can still be used.</p>}
        </div>
        <div className="space-y-3"><h3 className="text-sm font-semibold">Commission balances</h3>
          {context.sources.filter(source => source.remaining > 0.01 || Boolean(amounts[source.payout_id])).map(source => <div key={source.payout_id} className="rounded-md border p-3">
            {renderAmount(source.payout_id, `Deduct from ${source.loan_number} (${source.payout_type})`, source.remaining)}
            <p className="mt-1 text-xs text-muted-foreground">Net {currency(source.net)} · Cash paid {currency(source.cash)} · Recaptured {currency(source.recaptured)}</p>
          </div>)}
          {!context.sources.some(source => source.remaining > 0.01) && <p className="text-sm text-muted-foreground">No unpaid commission balances are available.</p>}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-muted p-3">
          <p role="status" aria-live="polite" className="font-semibold">Remaining to allocate: {currency(remaining)}</p>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" disabled={busy || unconfirmed || !workspace.enabled || invalidAmount || Boolean(claim.trim()) && (claimAmount === null || claimAmount <= 0)} onClick={() => void save(false)}>Save draft</Button>
            <Button type="submit" disabled={busy || unconfirmed || !canSubmit} onClick={() => void save(true)}>{busy ? 'Saving…' : 'Submit'}</Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">Submit requires a zero remaining allocation. Drafts do not reserve balances or stop commission payments.</p>
      </CardContent>
    </Card>}
    <Card>
      <CardHeader><CardTitle className="text-base">Recapture register</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <Label htmlFor="recapture-search">Search loan, loan officer, or status</Label><Input id="recapture-search" value={search} onChange={e => setSearch(e.target.value)} />
        {workspace.records.filter(record => `${record.loan_number} ${record.recipient_name} ${record.status}`.toLowerCase().includes(search.toLowerCase())).map(record => <div key={record.id} className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3">
          <div className="min-w-0 text-sm"><p className="font-medium">{record.loan_number} · {record.recipient_name}</p><p className="capitalize">{record.status} · EPO · {currency(record.target_amount)}</p>
            {record.status === 'draft' || record.status === 'cancelled' ? <p className="text-xs text-muted-foreground">{record.status === 'draft' ? 'Pending; no balances reserved.' : 'Cancelled; no balances deducted.'}</p> : <p className="text-xs text-muted-foreground">{record.status === 'reversed' && 'Restored; original allocation: '}Recovered from commissions: {currency(record.allocations.filter(a => a.kind === 'commission').reduce((sum, a) => sum + a.amount, 0))} · Absorbed by profitability: {currency(record.allocations.filter(a => a.kind === 'profitability').reduce((sum, a) => sum + a.amount, 0))}</p>}
          </div>
          <div className="flex gap-2">
            {record.status === 'draft' && record.origin_payout_id && workspace.enabled && <Button size="sm" variant="outline" disabled={busy || unconfirmed} onClick={() => void openOrigin(record.origin_payout_id!, record)}>Edit</Button>}
            {record.status === 'draft' && <Button size="sm" variant="outline" disabled={busy || unconfirmed} onClick={() => void perform(async () => {
              await cancelRecaptureDraft(record.id, record.version);
              if (draft?.id === record.id) {
                setContext(null);
                setDraft(null);
              }
              await onRefresh();
            })}>Cancel draft</Button>}
            <Button size="sm" variant="outline" onClick={event => {
              receiptTrigger.current = event.currentTarget;
              setReceipt(record);
              setReversalReason('');
              setError('');
            }}>View record</Button>
          </div>
        </div>)}
        {workspace.records.length === 0 && <p className="text-sm text-muted-foreground">No recaptures have been entered.</p>}
      </CardContent>
    </Card>
    <Dialog open={Boolean(receipt)} onOpenChange={handleReceiptOpenChange}>
      <DialogContent ref={receiptContent} className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" onCloseAutoFocus={event => {
        event.preventDefault();
        (receiptTrigger.current?.isConnected ? receiptTrigger.current : refreshButton.current)?.focus();
      }}>
        <DialogHeader><DialogTitle>Recapture record</DialogTitle><DialogDescription>{receipt?.loan_number} · {receipt?.recipient_name}</DialogDescription></DialogHeader>
        {receipt && <div className="space-y-4 text-sm">
          {error && <p role="alert" className="text-destructive">{error}</p>}
          <p className="capitalize">{receipt.status} · Early payoff · Target {currency(receipt.target_amount)}</p>
          {receipt.replacement_of && <p className="break-all text-xs text-muted-foreground">Replaces recapture {receipt.replacement_of}</p>}
          {receipt.status === 'reversed' && <p>Deductions restored. The original allocations are retained below.</p>}
          <p>Investor claim: {currency(receipt.claim_amount)} · Invoice: {receipt.invoice_reference || 'Not provided'}</p>
          {receipt.submission_snapshot && <p className="text-muted-foreground">At submission: original net commission {currency(receipt.submission_snapshot.origin.net)} · Cash paid {currency(receipt.submission_snapshot.origin.cash)} · Finalized premium {currency(receipt.submission_snapshot.origin.premium)}.</p>}
          {receipt.origin_cash_warning && <p role="alert" className="rounded-md border border-warning/30 p-3">The original cash payment evidence has changed since submission. Review the corrected payment history.</p>}
          {receipt.allocations.map((allocation, index) => <div key={index} className="flex flex-wrap justify-between gap-2 border-b pb-2">
            <span>{allocation.kind === 'profitability' ? 'Absorbed by LO Profitability' : `Recovered from commission ${'loan_number' in (allocation.source_snapshot || {}) ? (allocation.source_snapshot as { loan_number: string }).loan_number : ''}`}</span><strong>{currency(allocation.amount)}</strong>
          </div>)}
          {receipt.notes && <p className="whitespace-pre-wrap break-words">{receipt.notes}</p>}
          {(receipt.attachments || []).map(file => <Button key={file.id} variant="link" className="h-auto max-w-full whitespace-normal break-all p-0 text-left" disabled={busy}
            onClick={() => void perform(() => downloadRecaptureEvidence(file))}>{file.file_name}</Button>)}
          <Button variant="outline" onClick={() => {
            const text = [
              `Recapture ${receipt.id}`, `${receipt.loan_number} · ${receipt.recipient_name}`,
              `Status: ${receipt.status} · Reason: Early payoff (EPO)`, `Gross commission target: ${currency(receipt.target_amount)}`,
              `Investor claim: ${currency(receipt.claim_amount)} · Invoice: ${receipt.invoice_reference || 'Not provided'}`,
              `Invoice date: ${receipt.invoice_date || 'Not provided'} · Payoff date: ${receipt.payoff_date || 'Not provided'}`,
              ...(receipt.status === 'reversed' ? ['Deductions restored; original allocations below.'] : []),
              ...receipt.allocations.map(a => `${a.kind === 'profitability' ? 'Absorbed by profitability' : `Recovered from commission ${  (a.source_snapshot as { loan_number?: string })?.loan_number || ''}`}: ${currency(a.amount)}`),
              `Notes: ${receipt.notes}`, ...(receipt.reversal_reason ? [`Reversal: ${receipt.reversal_reason}`] : []),
              ...receipt.events.map(e => `${e.created_at} · ${e.event_type} · ${e.actor_name}`),
              ...(receipt.attachments || []).map(f => `Evidence: ${f.file_name} · ${f.size_bytes} bytes · SHA-256 (client): ${f.client_sha256}`)
            ].join('\n');
            downloadRecaptureBlob(new Blob([text], { type: 'text/plain;charset=utf-8' }), `recapture-${receipt.id}.txt`);
          }}>Download receipt</Button>
          {receipt.reversal_reason && <p>Reversal reason: {receipt.reversal_reason}</p>}
          <details><summary className="cursor-pointer font-medium">Audit history</summary><ul className="mt-2 space-y-2">{receipt.events.map(event => <li key={event.id}><span className="capitalize">{event.event_type}</span> by {event.actor_name} · {new Date(event.created_at).toLocaleString()}</li>)}</ul></details>
          {receipt.status === 'submitted' && <div className="space-y-2 border-t pt-3"><Label htmlFor="recapture-reversal">Reason for reversal — visible to the loan officer</Label><Textarea id="recapture-reversal" value={reversalReason} onChange={e => setReversalReason(e.target.value)} disabled={busy} />
            <Button variant="destructive" disabled={busy || !reversalReason.trim()} onClick={() => void perform(async () => {
              await reverseRecapture(receipt.id, reversalReason);
              setReceipt(null);
              await onRefresh();
              setMessage('Recapture reversed. Its deductions were restored.');
            })}>Reverse recapture</Button></div>}
          {receipt.status === 'reversed' && receipt.origin_payout_id && workspace.enabled && <Button disabled={busy} onClick={() => void openOrigin(receipt.origin_payout_id!, null, receipt.id)}>Create replacement</Button>}
        </div>}
      </DialogContent>
    </Dialog>
  </div>;
}
